// /care/start
//
// Public landing page for guided care. The whole page exists to do one
// thing: send the visitor to /care/get-started to get matched with a
// specialist. Anything else (WhatsApp, callback) is intentionally
// secondary — small, late, and visually quiet.
//
// We removed the older in-page intake form here on purpose. The
// canonical signup flow lives at /care/get-started; surfacing a parallel
// form on /care/start created two competing primary paths and made the
// page feel cluttered.
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'

import {
  Container,
  Eyebrow,
  Lede,
  SectionTitle,
} from './sections'
import {
  ArrowRight,
  ClipboardList,
  MessageCircle,
  PhoneCall,
  Repeat,
  ShieldCheck,
  Stethoscope,
  UserCheck,
} from './icons'

const WHATSAPP_PREFILL = 'Hi, I would like help finding the right care.'

function buildPrefilledWhatsappUrl(baseUrl, message) {
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}text=${encodeURIComponent(message)}`
}

export default function CareStartView() {
  const whatsappUrl = useMemo(
    () => buildPrefilledWhatsappUrl(APP_CONSTANTS.PATIENT_CARE_WHATSAPP_LINK, WHATSAPP_PREFILL),
    [],
  )

  return (
    <main id="main">
      {/* ── Hero ─ single, loud ask: get matched ───────────────── */}
      <section className="as-care-hero as-care-hero--centered">
        <Container>
          <div className="as-care-hero-stack">
            <Eyebrow>Find the right specialist</Eyebrow>
            <h1 className="as-h1 as-care-hero-title">
              Get matched with a{' '}
              <span className="as-brand-gradient">specialist</span>
              <br className="as-care-hero-br" />
              who fits.
            </h1>
            <Lede className="as-care-hero-lede">
              Tell us what kind of care you need. We&rsquo;ll line up vetted specialists
              and a clear next step — usually the same day.
            </Lede>

            <div className="as-care-hero-cta">
              <Link to="/care/get-started" className="as-btn as-btn-primary as-btn-xl">
                Get matched with a specialist <ArrowRight />
              </Link>
              <a
                href={whatsappUrl}
                className="as-care-hero-link"
                target="_blank"
                rel="noreferrer"
              >
                Or message us on WhatsApp instead
              </a>
            </div>

            <ul className="as-care-hero-trust" aria-label="What this means">
              <li className="as-trust-item">
                <ShieldCheck /> Confidential
              </li>
              <li className="as-trust-item">
                <UserCheck /> Reviewed by our care team
              </li>
              <li className="as-trust-item">
                <Repeat /> No commitment to book
              </li>
            </ul>

            <div className="as-care-note as-care-note--centered">
              If you or someone you know is in immediate danger, please contact local
              emergency services right away.
            </div>
          </div>
        </Container>
      </section>

      {/* ── How it works ─ 3 quiet steps that lead back to the CTA ── */}
      <section className="as-section as-section-white">
        <Container>
          <div className="as-care-howit-head">
            <Eyebrow>How it works</Eyebrow>
            <SectionTitle>Three steps. No back-and-forth.</SectionTitle>
          </div>
          <ol className="as-care-howit-grid">
            <li className="as-care-howit-step">
              <span className="as-care-howit-num">1</span>
              <div className="as-care-howit-icon">
                <ClipboardList />
              </div>
              <h3 className="as-h3">Tell us what you need</h3>
              <p className="as-p">
                Pick the kind of care that fits — therapy, psychiatry, hormonal,
                child care, or you&rsquo;re not sure.
              </p>
            </li>
            <li className="as-care-howit-step">
              <span className="as-care-howit-num">2</span>
              <div className="as-care-howit-icon">
                <ShieldCheck />
              </div>
              <h3 className="as-h3">Create a quick account</h3>
              <p className="as-p">
                One mobile number, one OTP. No emails, no long forms upfront.
              </p>
            </li>
            <li className="as-care-howit-step">
              <span className="as-care-howit-num">3</span>
              <div className="as-care-howit-icon">
                <Stethoscope />
              </div>
              <h3 className="as-h3">Meet a specialist</h3>
              <p className="as-p">
                We share matched specialists and a clear next step. You decide
                when, where, and how.
              </p>
            </li>
          </ol>
          <div className="as-care-howit-cta">
            <Link to="/care/get-started" className="as-btn as-btn-primary">
              Get matched with a specialist <ArrowRight />
            </Link>
          </div>
        </Container>
      </section>

      {/* ── Other ways to reach us ─ small, secondary ─────────── */}
      <section className="as-section as-section-alt">
        <Container>
          <div className="as-care-altways-head">
            <Eyebrow>Prefer to talk first?</Eyebrow>
            <SectionTitle>Other ways to reach our care team</SectionTitle>
          </div>
          <div className="as-care-altways">
            <a
              href={whatsappUrl}
              className="as-care-altway"
              target="_blank"
              rel="noreferrer"
            >
              <span className="as-care-altway-icon">
                <MessageCircle />
              </span>
              <span className="as-care-altway-body">
                <span className="as-care-kicker">Talk to us</span>
                <span className="as-h3">Message on WhatsApp</span>
                <span className="as-p">
                  Reply when it suits you — our care team will pick up.
                </span>
                <span className="as-link as-link-violet as-care-entry-cta">
                  Open WhatsApp <ArrowRight />
                </span>
              </span>
            </a>
            <a
              href={APP_CONSTANTS.SUPPORT_SCHEDULE_CALL}
              className="as-care-altway"
              target="_blank"
              rel="noreferrer"
            >
              <span className="as-care-altway-icon">
                <PhoneCall />
              </span>
              <span className="as-care-altway-body">
                <span className="as-care-kicker">Want a call?</span>
                <span className="as-h3">Get a call back</span>
                <span className="as-p">
                  Share your number and a good time — a coordinator will call you.
                </span>
                <span className="as-link as-link-violet as-care-entry-cta">
                  Pick a time <ArrowRight />
                </span>
              </span>
            </a>
          </div>
        </Container>
      </section>
    </main>
  )
}
